/**
 * Offer popup: opens after a delay, then stays away for a set number of hours so it does not
 * come back on every page or in every new tab. It closes with the X button, the Escape key or a
 * click outside the image.
 *
 * "Already shown" is remembered in localStorage, with a cookie and sessionStorage as fallbacks,
 * because any one of them can be blocked (in-app browsers, strict privacy settings) or be empty
 * in a new tab.
 */
class OfferPopup extends HTMLElement {
  connectedCallback() {
    if (this.#alreadySeen()) return;

    const delay = Number(this.dataset.delay) || 0;
    this.timer = setTimeout(() => this.#open(), delay);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-offer-popup-close]')) this.#close();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  get #key() {
    return this.dataset.storageKey;
  }

  get #hours() {
    return Number(this.dataset.hours) || 24;
  }

  #alreadySeen() {
    const key = this.#key;

    try {
      const shownAt = Number(localStorage.getItem(key));
      if (shownAt && Date.now() - shownAt < this.#hours * 3600 * 1000) return true;
    } catch (error) {
      // Blocked storage: fall through to the other checks.
    }

    if (document.cookie.split('; ').some((cookie) => cookie.startsWith(`${key}=`))) return true;

    try {
      if (sessionStorage.getItem(key) === '1') return true;
    } catch (error) {
      // Blocked storage: nothing more to check.
    }

    return false;
  }

  #markSeen() {
    const key = this.#key;

    try {
      localStorage.setItem(key, String(Date.now()));
    } catch (error) {
      // Blocked storage: the cookie below still covers it.
    }

    try {
      document.cookie = `${key}=1; max-age=${this.#hours * 3600}; path=/; SameSite=Lax`;
    } catch (error) {
      // Cookies blocked: the popup may show again, which is the only thing that can be done.
    }

    try {
      sessionStorage.setItem(key, '1');
    } catch (error) {
      // Blocked storage.
    }
  }

  #open() {
    this.hidden = false;
    this.#markSeen();
    document.addEventListener('keydown', this.#onKeydown);
    this.querySelector('.offer-popup__close')?.focus({ preventScroll: true });
  }

  #close() {
    this.hidden = true;
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#close();
  };
}

if (!customElements.get('offer-popup')) {
  customElements.define('offer-popup', OfferPopup);
}
